import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose as CloseIcon } from 'react-icons/md';
import { NavLink } from 'react-router-dom';
import { Button, Card } from '@knack/asterisk-react';

import { Route } from '@/enums';
import { useVideoResourcesQuery } from '@/hooks/api/queries/useVideoResourcesQuery';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useSession } from '@/hooks/useSession';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { VIDEO_LIMIT, VideoResource } from '@/pages/resources/getting-started';

export function GettingStarted({
  isDismissible,
  isHidden
}: {
  isDismissible: boolean;
  isHidden: boolean;
}) {
  const [t] = useTranslation();
  const session = useSession();
  const { data: videos, isLoading } = useVideoResourcesQuery(
    session.user.marketing?.questionnaire?.user_type,
    VIDEO_LIMIT
  );
  const [isDismissed, setIsDismissed] = useLocalStorage(
    LOCAL_STORAGE_KEYS.gettingStartedDismissed,
    'no'
  );

  if (isDismissed === 'yes' || isHidden) {
    return null;
  }

  return (
    <Card className="relative flex flex-col gap-4 sm:p-4">
      {isDismissible && (
        <Button
          intent="minimalStandalone"
          onClick={() => setIsDismissed('yes')}
          className="absolute right-3 top-3 p-2 opacity-70 transition-opacity hover:opacity-100"
        >
          <Button.Icon icon={CloseIcon} />
        </Button>
      )}

      <h3 className="text-xl font-medium text-emphasis">{t('components.apps.getting_started')}</h3>
      <div className="grid grid-cols-2 flex-row gap-4 sm:grid-cols-3 lg:flex">
        <NavLink
          className="flex flex-1 cursor-pointer flex-col gap-2"
          to={{ pathname: `/${Route.Resources}/${Route.GettingStarted}` }}
        >
          <div className="relative flex h-full min-h-12 w-full items-center justify-center overflow-hidden rounded-md bg-muted text-brand lg:min-h-16 xl:min-h-20">
            <svg
              width="38"
              height="28"
              viewBox="0 0 34 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.254772 20.7157C0.20812 21.1273 0.50395 21.4988 0.915528 21.5454C1.32711 21.5921 1.69858 21.2962 1.74523 20.8847L0.254772 20.7157ZM2.86667 4.33211L2.38464 3.75752C2.23732 3.88111 2.1431 4.05657 2.12144 4.24764L2.86667 4.33211ZM7.08202 1.77478C7.39936 1.50857 7.4408 1.03551 7.17459 0.71817C6.90837 0.400834 6.43531 0.359392 6.11797 0.625607L7.08202 1.77478ZM32.2548 20.8847C32.3014 21.2962 32.6729 21.5921 33.0845 21.5454C33.496 21.4988 33.7919 21.1273 33.7452 20.7157L32.2548 20.8847ZM31.1333 4.33211L31.8786 4.24764C31.8569 4.05657 31.7627 3.88111 31.6154 3.75752L31.1333 4.33211ZM27.882 0.625607C27.5647 0.359392 27.0916 0.400834 26.8254 0.71817C26.5592 1.03551 26.6006 1.50857 26.918 1.77478L27.882 0.625607ZM11.45 21.2002C11.45 23.8788 9.27858 26.0502 6.6 26.0502V27.5502C10.107 27.5502 12.95 24.7072 12.95 21.2002H11.45ZM6.6 26.0502C3.92142 26.0502 1.75 23.8788 1.75 21.2002H0.25C0.25 24.7072 3.09299 27.5502 6.6 27.5502V26.0502ZM1.75 21.2002C1.75 18.5216 3.92142 16.3502 6.6 16.3502V14.8502C3.09299 14.8502 0.25 17.6932 0.25 21.2002H1.75ZM6.6 16.3502C9.27858 16.3502 11.45 18.5216 11.45 21.2002H12.95C12.95 17.6932 10.107 14.8502 6.6 14.8502V16.3502ZM12.7984 20.3757C13.6381 18.9241 15.2058 17.9502 17 17.9502V16.4502C14.6487 16.4502 12.5968 17.7286 11.5 19.6247L12.7984 20.3757ZM17 17.9502C18.7943 17.9502 20.3619 18.9241 21.2016 20.3757L22.5001 19.6247C21.4032 17.7286 19.3514 16.4502 17 16.4502V17.9502ZM32.25 21.2002C32.25 23.8788 30.0786 26.0502 27.4 26.0502V27.5502C30.907 27.5502 33.75 24.7072 33.75 21.2002H32.25ZM27.4 26.0502C24.7214 26.0502 22.55 23.8788 22.55 21.2002H21.05C21.05 24.7072 23.893 27.5502 27.4 27.5502V26.0502ZM22.55 21.2002C22.55 18.5216 24.7214 16.3502 27.4 16.3502V14.8502C23.893 14.8502 21.05 17.6932 21.05 21.2002H22.55ZM27.4 16.3502C30.0786 16.3502 32.25 18.5216 32.25 21.2002H33.75C33.75 17.6932 30.907 14.8502 27.4 14.8502V16.3502ZM1.74523 20.8847L3.61189 4.41658L2.12144 4.24764L0.254772 20.7157L1.74523 20.8847ZM3.34869 4.9067L7.08202 1.77478L6.11797 0.625607L2.38464 3.75752L3.34869 4.9067ZM33.7452 20.7157L31.8786 4.24764L30.3881 4.41658L32.2548 20.8847L33.7452 20.7157ZM31.6154 3.75752L27.882 0.625607L26.918 1.77478L30.6513 4.9067L31.6154 3.75752Z"
                fill="#982A86"
              />
            </svg>
          </div>
          <div className="text-xs font-medium">{t('components.apps.help_resources')}</div>
        </NavLink>
        <div className="hidden min-h-full w-px bg-subtle lg:block" />
        {Array.from({ length: VIDEO_LIMIT }, (_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <VideoResource key={index} isLoading={isLoading} video={videos?.[index]} />
        ))}
      </div>
    </Card>
  );
}
